import { parseAmount } from "./convertNumbers";
import { formatNumberWithSpaces } from "./numberFormatter";

interface BusinessTransactionDetail {
  heroZarAmount: string;
  listAmount: string;
  transactionType: string;
  statusLabel: string;
  statusTone: "complete" | "pending" | "other";
  action: "Buy" | "Sell" | "Send" | "Receive";
  transactionMethod: string;
  transactionId: string;
  dateLine: string;
  timeLine: string;
  serviceCharge: string;
  totalCost: string;
  showSplitPayment: boolean;
}

export interface BusinessTransactionRow {
  id: string;
  direction: "sent" | "received";
  actionLabel: string;
  actionLabelMobile: string;
  time: string;
  primaryAmount: string;
  secondaryAmount: string;
  status: string;
  sortAmount?: number;
  sortDate?: number;
  filterType?: string;
  filterStatus?: string;
  detail: BusinessTransactionDetail;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

function readDecimal(value: unknown): unknown {
  if (isRecord(value) && "$numberDecimal" in value) {
    return value.$numberDecimal;
  }
  return value;
}

function readAssetSymbol(value: unknown): string {
  if (isRecord(value)) {
    const symbol = value.symbol ?? value.assetSymbol ?? value.asset;
    return symbol === undefined || symbol === null
      ? ""
      : String(symbol).trim().toUpperCase();
  }

  return value === undefined || value === null
    ? ""
    : String(value).trim().toUpperCase();
}

function formatZar(value: unknown): string {
  const parsed = parseAmount(readDecimal(value));
  return `R ${formatNumberWithSpaces(parsed, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MONGO_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

/** Trading-pair subtitle for list rows; omits Mongo ids stored in `transactionRef.pair`. */
export function formatBusinessTransactionPairLabel(pair: unknown): string {
  const raw = String(pair ?? "").trim();
  if (!raw || MONGO_OBJECT_ID_PATTERN.test(raw)) {
    return "";
  }

  if (!/[-/.]/.test(raw)) {
    return "";
  }

  return raw
    .toUpperCase()
    .split(/[-/.]+/)
    .filter(Boolean)
    .join(" · ");
}

function formatAmountDisplay(value: unknown): string {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "—";
  }

  const raw = String(value).trim();
  const parts = raw.split(/\s+/);
  if (parts.length >= 2) {
    const symbol = parts[parts.length - 1].toUpperCase();
    const amount = parts.slice(0, -1).join(" ");
    return `${formatNumberWithSpaces(parseAmount(amount), {
      minimumFractionDigits: 2,
      maximumFractionDigits: symbol === "BTC" ? 6 : 2,
    })} ${symbol}`;
  }

  return raw;
}

export function resolveBusinessTxStatusTone(
  statusLabel: string,
  fallbackTone?: "complete" | "pending" | "other" | null
): "complete" | "pending" | "other" {
  if (fallbackTone === "complete" || fallbackTone === "pending") {
    return fallbackTone;
  }
  const normalized = statusLabel.toUpperCase();
  if (normalized.includes("COMPLETE")) {
    return "complete";
  }
  if (normalized.includes("PENDING") || normalized.includes("AWAITING")) {
    return "pending";
  }
  return "other";
}

export function getBusinessWalletTransactionSortAmount(tx: unknown): number {
  if (!isRecord(tx)) {
    return 0;
  }

  const ref = getRecord(tx.transactionRef);
  const subtype = String(tx.transactionSubtype || "").toLowerCase();
  const type = String(tx.transactionType || "");

  if (type === "UserTrade") {
    if (subtype === "buy") {
      return parseAmount(
        readDecimal(ref?.incomingAmount) ?? readDecimal(ref?.total) ?? tx.amount
      );
    }
    if (subtype === "sell") {
      return parseAmount(
        readDecimal(ref?.outgoingAmount) ??
          readDecimal(ref?.incomingAmount) ??
          readDecimal(ref?.total) ??
          tx.amount
      );
    }
  }

  return parseAmount(
    readDecimal(ref?.amount) ?? readDecimal(ref?.total) ?? tx.amount
  );
}

export function mapBusinessApiTxToRow(
  transaction: unknown
): BusinessTransactionRow | null {
  if (!isRecord(transaction)) {
    return null;
  }

  const ref = getRecord(transaction.transactionRef);
  const id = String(
    transaction._id ?? ref?._id ?? transaction.id ?? `tx-${Date.now()}`
  );
  const type =
    transaction.transactionType !== undefined
      ? String(transaction.transactionType)
      : transaction.type !== undefined
        ? String(transaction.type)
        : "";
  const subtype = String(
    transaction.transactionSubtype || transaction.action || ""
  ).toLowerCase();
  const activity = String(transaction.activity || "").toLowerCase();
  const pairLabel = formatBusinessTransactionPairLabel(ref?.pair);

  const canonicalType = type.toLowerCase();
  let isOutgoing =
    activity === "outgoing" ||
    /send|withdraw|out|fiat.*withdraw/i.test(type) ||
    subtype.includes("send") ||
    subtype.includes("withdraw") ||
    subtype.includes("burn") ||
    subtype === "sell";
  if (activity === "incoming") {
    isOutgoing = false;
  }

  const assetSymbol =
    readAssetSymbol(transaction.assetSymbol) ||
    readAssetSymbol(transaction.asset) ||
    readAssetSymbol(ref?.incomingAsset) ||
    readAssetSymbol(ref?.outgoingAsset);

  let actionLabel = type || "Transaction";
  let actionLabelMobile = type || "Tx";
  if (type === "UserTrade" || canonicalType === "trade") {
    if (subtype === "buy") {
      actionLabel = assetSymbol ? `Buy ${assetSymbol}` : "Buy";
      actionLabelMobile = "Buy";
    } else if (subtype === "sell") {
      actionLabel = assetSymbol ? `Sell ${assetSymbol}` : "Sell";
      actionLabelMobile = "Sell";
    } else {
      actionLabel = assetSymbol ? `Trade ${assetSymbol}` : "Trade";
      actionLabelMobile = "Trade";
    }
  } else if (subtype) {
    const label = subtype
      .split(/[_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
    actionLabel = assetSymbol ? `${label} ${assetSymbol}` : label;
    actionLabelMobile = label || "Tx";
  }

  const amountValue = readDecimal(transaction.amount);
  const rawPrimary =
    amountValue !== undefined &&
    amountValue !== null &&
    String(amountValue).trim() !== ""
      ? [amountValue, assetSymbol].filter(Boolean).join(" ")
      : [readDecimal(transaction.amount), assetSymbol]
          .filter(Boolean)
          .join(" ");
  const primaryAmount = formatAmountDisplay(rawPrimary);
  const createdAt =
    typeof transaction.createdAt === "string"
      ? new Date(transaction.createdAt)
      : null;
  const hasCreatedAt = createdAt !== null && !Number.isNaN(createdAt.getTime());
  const status =
    transaction.status !== undefined ? String(transaction.status) : "—";
  const heroAmount =
    primaryAmount !== "—"
      ? primaryAmount
      : ref?.total !== undefined
        ? formatZar(ref.total)
        : "—";

  return {
    id,
    direction: isOutgoing ? "sent" : "received",
    actionLabel,
    actionLabelMobile,
    time: hasCreatedAt
      ? createdAt.toLocaleString("en-ZA", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "",
    primaryAmount,
    secondaryAmount: pairLabel,
    status,
    sortAmount: getBusinessWalletTransactionSortAmount(transaction),
    sortDate: hasCreatedAt ? createdAt.getTime() : 0,
    filterType: subtype || canonicalType,
    filterStatus: status.toLowerCase(),
    detail: {
      heroZarAmount: heroAmount,
      listAmount: primaryAmount,
      transactionType:
        type === "UserTrade"
          ? subtype === "buy"
            ? "Buy"
            : subtype === "sell"
              ? "Sell"
              : "Trade"
          : type || "—",
      statusLabel: status.toUpperCase(),
      statusTone: resolveBusinessTxStatusTone(status),
      action:
        subtype === "buy"
          ? "Buy"
          : subtype === "sell"
            ? "Sell"
            : isOutgoing
              ? "Send"
              : "Receive",
      transactionMethod: pairLabel || "—",
      transactionId: id,
      dateLine: hasCreatedAt
        ? createdAt.toLocaleDateString("en-ZA", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "—",
      timeLine: hasCreatedAt
        ? createdAt.toLocaleTimeString("en-ZA", {
            timeStyle: "medium",
          })
        : "",
      serviceCharge:
        ref?.serviceFee !== undefined ? formatZar(ref.serviceFee) : "R 0.00",
      totalCost: primaryAmount || heroAmount,
      showSplitPayment: false,
    },
  };
}
